// @flow
import type { IsFqdnAvailable } from '../../core/fqdn';
import type { SendAnalytics } from '../../core/ga';

import * as r from 'ramda';
import * as most from 'most';
import * as mostc from '../../crosscutting/mostc';
import { select, combineEpics } from 'redux-most';
import * as signals from '../signals';
import * as messages from '../../domain/messages';
import { fromEagerPromise, ignoreElements } from 'most-adjunct';
import { mapFqdnAvailableToStatus, isPremium } from '../../domain/purchase';
import {
  getRawSuggestions,
  getRawExact,
} from '../../domain/selectors';
import { Hook } from '../../core/constants';

export const verifyDomainEpic = (
  isFqdnAvailable: IsFqdnAvailable,
) => r.pipe(
  select(signals.PURCHASE_DOMAIN),
  mostc.map((action) => {
    const {
      payload: {
        fqdn,
      },
    } = action;

    const verifying$ = most.of(messages.verifying(fqdn));

    const verify$ = r.pipe(
      (fqdn) => fromEagerPromise(() => isFqdnAvailable(fqdn)),
      mostc.map((result) => messages.verified(fqdn, result)),
    )(fqdn);

    return most.concat(
      verifying$,
      verify$,
    );
  }),
  most.join,
);

export const trackPurchaseEpic = (
  sendAnalytics: SendAnalytics,
) => (actions$, store) => r.pipe(
  select(messages.VERIFIED),
  mostc.tap((action) => {
    const {
      payload: {
        fqdn,
        available,
      },
    } = action;
    // if the domain isn't available then we don't need to
    // collect any data
    if (available !== true) {
      return;
    }

    const state = store.getState();
    const domains = getRawSuggestions(state);
    const exactDomain = getRawExact(state);

    const domain = r.pipe(
      r.prepend(exactDomain),
      r.filter(Boolean),
      r.find(r.propEq('fqdn', fqdn)),
    )(domains);

    // and if we can't find the domain in question, we just stop here
    if (domain == null) {
      return;
    }

    sendAnalytics({
      hitType: 'event',
      eventCategory: 'Basket',
      eventAction: 'added',
      // eventLabel: payload.fqdn,
    });

    r.when(
      r.has('add_to_cart_callback'),
      () => {
        if (navigator.sendBeacon != null) {
          navigator.sendBeacon(domain.add_to_cart_callback);
        }
      },
    )(domain);
  }),
  ignoreElements,
)(actions$);

export const emitPurchaseEpic = () => (actions$, store) => r.pipe(
  select(messages.VERIFIED),
  mostc.map((action) => {
    const {
      payload: {
        fqdn,
        available,
      },
    } = action;

    const message = messages.verifyingComplete(
      fqdn,
      mapFqdnAvailableToStatus(available),
    );

    if (available !== true) {
      return most.of(message);
    }

    const state = store.getState();
    const domains = getRawSuggestions(state);
    const exactDomain = getRawExact(state);

    const domain = r.pipe(
      r.prepend(exactDomain),
      r.filter(Boolean),
      r.find(r.propEq('fqdn', fqdn)),
    )(domains);

    if (domain == null) {
      return most.of(message);
    }

    const filter = r.both(
      r.pathEq([ 'payload', 'hook' ], Hook.ADD_TO_BASKET),
      r.pathEq([ 'payload', 'payload', 'fqdn' ], fqdn),
    );

    // we return an emit signal which goes off and dispatches an event
    // to the window
    // then we have to wait for a success/failed message before we can
    // emit the verifyingComplete message

    const emit$ = most.of(
      signals.emit(
        Hook.ADD_TO_BASKET,
        {
          fqdn,
          premium: isPremium(domain),
          type: domain.type,
        },
        true,
      ),
    );

    const success$ = r.pipe(
      select(messages.EMIT_SUCCESS),
      mostc.filter(filter),
      mostc.map(r.always(message)),
    )(actions$);

    const failed$ = r.pipe(
      select(messages.EMIT_FAILED),
      mostc.filter(filter),
      mostc.map(r.always(messages.verifyingFailed(fqdn))),
    )(actions$);

    return most.mergeArray([
      emit$,
      success$,
      failed$,
    ]);
  }),
  most.join,
)(actions$);

export default (
  isFqdnAvailable: IsFqdnAvailable,
  sendAnalytics: SendAnalytics,
) => combineEpics([
  verifyDomainEpic(isFqdnAvailable),
  trackPurchaseEpic(sendAnalytics),
  emitPurchaseEpic(),
]);
